import { useCallback, useEffect } from 'react';
import type React from 'react';

import useInputCoordinateMapper from './useInputCoordinateMapper';
import type { InputLayerDelegate } from './inputManagerTypes';

type DrawingEditorCanvasInputManagerProps = {
    delegate?: InputLayerDelegate | null;
    drawingCanvasRootRef: React.RefObject<HTMLElement>;
    inputLayerRef: React.RefObject<HTMLElement>;
    canvasScale?: number;
    viewBoxX?: number;
    viewBoxY?: number;
    zoomScale?: number;
};

const DrawingEditorCanvasInputManager = ({
    delegate,
    drawingCanvasRootRef,
    inputLayerRef,
    canvasScale,
    viewBoxX,
    viewBoxY,
    zoomScale,
}: DrawingEditorCanvasInputManagerProps) => {
    const { mapPointerEvent } = useInputCoordinateMapper({
        drawingCanvasRootRef,
        inputLayerRef,
        canvasScale,
        viewBoxX,
        viewBoxY,
        zoomScale,
    });

    /**
     * Maps a DOM pointer event into normalized coordinates and forwards it to the active tool delegate.
     */
    const delegatePointerEvent = useCallback(
        (eventName: keyof InputLayerDelegate, event: PointerEvent) => {
            if (!delegate) return;

            const handler = delegate[eventName];
            if (!handler) return;

            const coordinateContext = mapPointerEvent(event);

            handler({
                event,
                ...coordinateContext,
            });
        },
        [delegate, mapPointerEvent],
    );

    /**
     * Starts pointer capture on input down so drag lifecycles remain stable while the pointer moves.
     */
    const onPointerDown = useCallback(
        (event: PointerEvent) => {
            const inputLayerNode = event.currentTarget as HTMLElement | null;
            if (!inputLayerNode) return;

            if (event.pointerId !== undefined) {
                inputLayerNode.setPointerCapture(event.pointerId);
            }
            delegatePointerEvent('onPointerDown', event);
        },
        [delegatePointerEvent],
    );

    /**
     * Forwards pointer move events to the active delegate.
     */
    const onPointerMove = useCallback(
        (event: PointerEvent) => {
            delegatePointerEvent('onPointerMove', event);
        },
        [delegatePointerEvent],
    );

    /**
     * Safely releases pointer capture if this pointer is currently captured by the input layer.
     */
    const releasePointerCaptureIfRequired = useCallback((event: PointerEvent) => {
        const inputLayerNode = event.currentTarget as HTMLElement | null;
        if (!inputLayerNode) return;
        if (event.pointerId === undefined) return;
        if (!inputLayerNode.hasPointerCapture(event.pointerId)) return;

        inputLayerNode.releasePointerCapture(event.pointerId);
    }, []);

    /**
     * Ends pointer interactions and forwards the normalized pointer up event.
     */
    const onPointerUp = useCallback(
        (event: PointerEvent) => {
            releasePointerCaptureIfRequired(event);
            delegatePointerEvent('onPointerUp', event);
        },
        [delegatePointerEvent, releasePointerCaptureIfRequired],
    );

    /**
     * Cleans up capture state when pointer interactions are cancelled by the browser.
     */
    const onPointerCancel = useCallback(
        (event: PointerEvent) => {
            releasePointerCaptureIfRequired(event);
            delegatePointerEvent('onPointerCancel', event);
        },
        [delegatePointerEvent, releasePointerCaptureIfRequired],
    );

    useEffect(() => {
        const inputLayerNode = inputLayerRef?.current;
        if (!inputLayerNode) return;

        inputLayerNode.addEventListener('pointerdown', onPointerDown);
        inputLayerNode.addEventListener('pointermove', onPointerMove);
        inputLayerNode.addEventListener('pointerup', onPointerUp);
        inputLayerNode.addEventListener('pointercancel', onPointerCancel);

        return () => {
            inputLayerNode.removeEventListener('pointerdown', onPointerDown);
            inputLayerNode.removeEventListener('pointermove', onPointerMove);
            inputLayerNode.removeEventListener('pointerup', onPointerUp);
            inputLayerNode.removeEventListener('pointercancel', onPointerCancel);
        };
    }, [inputLayerRef, onPointerCancel, onPointerDown, onPointerMove, onPointerUp]);

    return null;
};

export default DrawingEditorCanvasInputManager;
